import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { externalLawyersService, ExternalLawyer, ExternalLawyerInput } from '@/api/external-lawyers'
import { useToast } from '@/hooks/use-toast'

interface ExternalLawyerDialogProps {
  open: boolean
  lawyer?: ExternalLawyer
  onClose: () => void
  onSaved: () => void
}

export function ExternalLawyerDialog({ open, lawyer, onClose, onSaved }: ExternalLawyerDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const isEdit = !!lawyer

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [saving, setSaving] = useState(false)
  const [nameError, setNameError] = useState('')

  useEffect(() => {
    if (open) {
      setName(lawyer?.name ?? '')
      setEmail(lawyer?.email ?? '')
      setNameError('')
    }
  }, [open, lawyer])

  const handleSave = async () => {
    if (!name.trim()) {
      setNameError(t('ext_name_required'))
      return
    }
    setNameError('')

    const payload: ExternalLawyerInput = {
      name: name.trim(),
      email: email.trim() || null,
    }

    setSaving(true)
    try {
      if (isEdit) {
        await externalLawyersService.update(lawyer!.id, payload)
        toast({ title: t('ext_updated') })
      } else {
        await externalLawyersService.create(payload)
        toast({ title: t('ext_created') })
      }
      onSaved()
      onClose()
    } catch (err: any) {
      const msg =
        err?.response?.data?.name?.[0] ??
        err?.response?.data?.non_field_errors?.[0] ??
        t('error')
      toast({ title: msg, variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('ext_edit') : t('ext_add')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label className="text-xs">{t('ext_col_name')} *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('ext_name_placeholder')}
              className="h-9 text-sm"
            />
            {nameError && <p className="text-xs text-destructive">{nameError}</p>}
          </div>

          <div className="space-y-1">
            <Label className="text-xs">{t('ext_col_email')}</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('ext_email_placeholder')}
              className="h-9 text-sm"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? t('loading') : t('save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
